import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import {
  getMsoCases, getMsoData, verifyMso, regenerateMso, getDocuments, getPdfUrl,
  getFacilities, getPriorityFacilities, savePriorityFacilities,
} from "../lib/api";

/* Manager Sign Off reporting.
 *
 * Mirrors the IDR page: filters, count cards that double as filters, a table,
 * and a drawer for verification. The drawer differs in two ways. The document
 * pane carries all three sources MSO reads rather than one, and verification
 * is a two-person action: the first reviewer's verify is the clinical
 * sign-off, a second reviewer's is the final. The server derives which stage
 * a verify is and refuses the same person twice, so this page never sends a
 * stage and never decides who is allowed to go next.
 */

const MSO_STATUS = {
  pending:  { label: "Pending",  color: "#D97706" },
  ready:    { label: "Ready",    color: "#2563EB" },
  approved: { label: "Approved", color: "#059669" },
  rejected: { label: "Rejected", color: "#DC2626" },
};
// 1180 for the drawer itself plus room for the page behind it.
const DOCS_MIN_WIDTH = 1240;

const STATUS_ORDER = ["pending", "ready", "approved", "rejected"];

/* The three documents MSO reads. sourceType is how the portal labels them on
 * the document record; the screenshots archive is the only mandatory one. */
const SOURCE_TABS = [
  { key: "Usmon Screenshots", label: "Screenshots" },
  { key: "Technical Report",  label: "Tech Report" },
  { key: "Usmon Chat Log",    label: "Chat" },
];

const input = {
  width: "100%", height: 34, padding: "0 8px", fontSize: 13,
  border: "1px solid var(--border)", borderRadius: 6,
  background: "var(--white)", color: "var(--text-primary)",
};

const th = {
  padding: "8px 12px", textAlign: "left", fontSize: 10,
  fontFamily: "var(--font-mono)", color: "var(--text-tertiary)",
  textTransform: "uppercase", fontWeight: 500, whiteSpace: "nowrap",
};
const td = { padding: "10px 12px", fontSize: 12.5, verticalAlign: "middle" };

// Row actions share a width so the column does not jag as the label changes
// between states, matching the IDR page.
const rowBtn = { minWidth: 92, textAlign: "center" };

// Verify carries amber so a row still awaiting a sign-off says so twice, in
// its dates and in its action. A case with both sign-offs recorded has
// nothing outstanding, so View stays neutral and recedes.
const verifyBtn = {
  ...rowBtn,
  background: "#FFFBEB",
  border: "1px solid #FDE68A",
  color: "#B45309",
  fontWeight: 600,
};

// Quarter presets, mirroring the IDR and Audit pages.
function quarterOptions(count) {
  const out = [];
  const now = new Date();
  let y = now.getFullYear();
  let q = Math.floor(now.getMonth() / 3);
  for (let i = 0; i < count; i++) {
    const startMonth = q * 3;
    const from = new Date(y, startMonth, 1);
    const to = new Date(y, startMonth + 3, 0);
    const iso = (d) => d.toISOString().slice(0, 10);
    out.push({ label: "Q" + (q + 1) + " " + y, from: iso(from), to: iso(to) });
    q -= 1;
    if (q < 0) { q = 3; y -= 1; }
  }
  return out;
}

/* A time the technologist never typed is not a disagreement, it is an absence.
   The parser deliberately ignores the leading "2:29 PM Name, MD:" on a chat
   line, because that is when the message was sent, stamped in Eastern, while
   the tech documents in the facility timezone. So missing_chat means no time
   was written down, and must not read as drift. */
const TIME_STATUS_HELP = {
  match: "The documented time matches USMON exactly.",
  drift: "The time the technologist typed differs from USMON.",
  missing_chat: "No time was documented in the chat for this event. The chat's own send timestamp is not used, because it is stamped in a different timezone from the one the technologist documents in.",
  missing_usmon: "USMON has no time recorded for this event.",
};

function TIME_STATUS_COLOR(status) {
  if (status === "match") return "#059669";
  if (status === "drift") return "#D97706";
  return "#94A3B8";
}

function TIME_STATUS_LABEL(t) {
  if (t.status === "match") return "✓";
  if (t.status === "missing_chat") return "not documented";
  if (t.status === "missing_usmon") return "not in USMON";
  if (t.delta_minutes != null) return t.delta_minutes + " min";
  return t.status;
}

function StatusPill({ status }) {
  const cfg = MSO_STATUS[status] || { label: status || "None", color: "#94A3B8" };
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, color: cfg.color,
      border: "1px solid " + cfg.color, padding: "2px 8px",
      borderRadius: 10, whiteSpace: "nowrap",
    }}>{cfg.label}</span>
  );
}

/* A sign-off cell. A blank date is not a failure, so it reads as a dash
 * rather than as something wrong. */
function SignOffCell({ date, by }) {
  if (!date) return <span style={{ fontSize: 11, color: "#94A3B8" }}>-</span>;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 1 }}>
      <span style={{ fontSize: 12 }}>{date}</span>
      {by && <span style={{ fontSize: 10.5, color: "#64748B" }}>{by}</span>}
    </span>
  );
}

function SortArrow({ active, dir }) {
  if (!active) return <span style={{ opacity: 0.25, fontSize: 9 }}>&#9650;</span>;
  return <span style={{ fontSize: 9 }}>{dir === "asc" ? "▲" : "▼"}</span>;
}

export default function MSOReporting() {
  const { accounts } = useMsal();
  const account = accounts && accounts[0];
  const me = (account && (account.username || account.name)) || "";

  const [cases, setCases] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [postSentAvailable, setPostSentAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [search, setSearch] = useState("");
  const [statusF, setStatusF] = useState([]);
  const [sort, setSort] = useState({ key: "serviceDate", dir: "desc" });

  // The client's 24-hour sign-off list, and the filter that uses it.
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [editingList, setEditingList] = useState(false);

  const [verifying, setVerifying] = useState(null);
  const [mso, setMso] = useState(null);
  const [msoLoading, setMsoLoading] = useState(false);
  const [msoError, setMsoError] = useState("");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyNotes, setVerifyNotes] = useState("");

  const [docTab, setDocTab] = useState(SOURCE_TABS[0].key);
  const [docUrls, setDocUrls] = useState({});
  const [docsLoading, setDocsLoading] = useState(false);
  // The open drawer is 1180 wide. Below that the panes overlap instead of
  // shrinking, so a narrow window opens with the documents collapsed and the
  // reader shows them deliberately. Read once at mount, not on every render.
  const [showDocs, setShowDocs] = useState(
    () => (typeof window === "undefined" || window.innerWidth >= DOCS_MIN_WIDTH));

  const quarters = useMemo(() => quarterOptions(8), []);

  const reload = useCallback(() => {
    setLoading(true);
    setError("");
    return getMsoCases(from, to)
      .then((r) => {
        setCases((r && r.cases) || []);
        setPostSentAvailable(!!(r && r.postSentAvailable));
      })
      .catch((e) => setError(e.message || "Could not load cases"))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < DOCS_MIN_WIDTH) setShowDocs(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // The drawer's comparison and its three documents.
  useEffect(() => {
    if (!verifying) {
      setMso(null); setMsoError(""); setDocUrls({}); setVerifyNotes("");
      setAlerts([]);
      return;
    }
    let cancelled = false;
    setMsoLoading(true);
    setMsoError("");
    setMso(null);
    getMsoData(verifying.patientId)
      .then((r) => { if (!cancelled) setMso(r); })
      .catch((e) => { if (!cancelled) setMsoError(e.message || "Could not load the comparison"); })
      .finally(() => { if (!cancelled) setMsoLoading(false); });

    setDocsLoading(true);
    setDocUrls({});
    setAlerts([]);
    (async () => {
      try {
        const all = await getDocuments({});
        const pid = String(verifying.patientId);
        const found = {};
        const gathered = [];
        const seen = new Set();
        for (const d of all || []) {
          if (String(d.patientId) !== pid) continue;

          // detect_alerts writes these onto the search record before the QA
          // checks run, so they arrive with the document and need no second
          // request. Collected from every file of the case, since the
          // detector runs per file.
          for (const a of d.alertDetails || []) {
            const key = [a.type, a.time, a.modality, a.description].join("|");
            if (seen.has(key)) continue;
            seen.add(key);
            gathered.push({ ...a, manual: (d.alertStatus === "MANUAL_ALERT") });
          }

          const st = d.sourceType || "";
          if (!SOURCE_TABS.some(t => t.key === st)) continue;
          if (found[st]) continue;
          const r = await getPdfUrl(d.id);
          if (r && r.url) found[st] = r.url;
        }
        if (!cancelled) { setDocUrls(found); setAlerts(gathered); }
      } catch {
        // A missing document is an empty pane, not a page error.
      } finally {
        if (!cancelled) setDocsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [verifying]);

  const counts = useMemo(() => {
    const acc = {};
    for (const k of STATUS_ORDER) acc[k] = 0;
    for (const c of cases) {
      if (acc[c.status] !== undefined) acc[c.status] += 1;
    }
    return acc;
  }, [cases]);

  const filtered = useMemo(() => {
    let out = cases;
    if (priorityOnly) out = out.filter(c => c.priorityFacility);
    if (statusF.length) {
      const keys = STATUS_ORDER.filter(k => statusF.includes(MSO_STATUS[k].label));
      out = out.filter(c => keys.includes(c.status));
    }
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(c =>
        String(c.patientId).includes(q) ||
        (c.facility || "").toLowerCase().includes(q) ||
        (c.surgeonName || "").toLowerCase().includes(q) ||
        (c.techName || "").toLowerCase().includes(q)
      );
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...out].sort((a, b) => {
      const av = a[sort.key] == null ? "" : a[sort.key];
      const bv = b[sort.key] == null ? "" : b[sort.key];
      if (av === bv) return String(a.patientId).localeCompare(String(b.patientId));
      return String(av).localeCompare(String(bv), undefined, { numeric: true }) * dir;
    });
  }, [cases, statusF, search, sort, priorityOnly]);

  const toggleSort = (key) => setSort(s =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });

  // Which stage this reviewer's verify would be, and whether they may do it.
  const verifications = (mso && mso.verifications) || [];
  const nextStage = verifications.length === 0 ? "clinical"
    : verifications.length === 1 ? "final" : null;
  const alreadyVerifiedByMe = verifications.some(
    v => (v.by || "").toLowerCase() === (me || "").toLowerCase());
  const canVerify = !!nextStage && !alreadyVerifiedByMe && !verifyBusy && !!mso;

  // Site matching is decided during the comparison and stored, unlike the
  // times, which are recomputed per request. So a config change such as a
  // site override only reaches a case that is re-run, and a reviewer
  // working from this page needs to be able to do that here. Closed once
  // signed, matching the case page: a signed comparison should not change
  // underneath the signature.
  const [regenerating, setRegenerating] = useState(false);
  const canRegenerate = !!mso && !!nextStage && !regenerating;

  const doRegenerate = useCallback(async () => {
    if (!verifying || !canRegenerate) return;
    setRegenerating(true);
    setMsoError("");
    try {
      await regenerateMso(verifying.patientId);
      setMso(prev => ({ ...(prev || {}), status: "running" }));
      setTimeout(() => {
        getMsoData(verifying.patientId)
          .then(setMso)
          .catch(() => {})
          .finally(() => setRegenerating(false));
      }, 5000);
    } catch (e) {
      setMsoError(e.message || "Could not start the comparison");
      setRegenerating(false);
    }
  }, [verifying, canRegenerate]);

  const doVerify = async () => {
    if (!canVerify) return;
    setVerifyBusy(true);
    setMsoError("");
    try {
      const r = await verifyMso(verifying.patientId, {
        notes: verifyNotes, version: mso.version,
      });
      setMso(r);
      setVerifyNotes("");
      setNotice(
        (r && r.verification && r.verification.stage === "final")
          ? "Final sign-off recorded for case " + verifying.patientId
          : "Clinical sign-off recorded for case " + verifying.patientId);
      reload();
    } catch (e) {
      setMsoError(e.message || "Could not record the verification");
    } finally {
      setVerifyBusy(false);
    }
  };

  const comparison = (mso && mso.comparison) || {};
  const modalityGroups = comparison.modality_groups || [];
  const timeChecks = comparison.time_checks || [];
  const timeSummary = comparison.time_summary || null;

  return (
    <>
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">Manager Sign Off</div>
          <div className="page-subtitle">
            Screenshot and report verification, with two-person sign-off
          </div>
        </div>
      </div>

      <div className="page-body">
        {/* Filters */}
        <div className="card" style={{ padding: 16, marginBottom: 16, display: "flex",
          gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ minWidth: 150 }}>
            <label style={{ fontSize: 11, color: "#64748B", display: "block", marginBottom: 4 }}>QUARTER</label>
            <select style={input} value={from + "|" + to}
              onChange={(e) => {
                const parts = e.target.value.split("|");
                setFrom(parts[0]); setTo(parts[1]);
              }}>
              <option value="|">All dates</option>
              {quarters.map(q => (
                <option key={q.label} value={q.from + "|" + q.to}>{q.label}</option>
              ))}
            </select>
          </div>
          <div style={{ minWidth: 140 }}>
            <label style={{ fontSize: 11, color: "#64748B", display: "block", marginBottom: 4 }}>FROM</label>
            <input type="date" style={input} value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div style={{ minWidth: 140 }}>
            <label style={{ fontSize: 11, color: "#64748B", display: "block", marginBottom: 4 }}>TO</label>
            <input type="date" style={input} value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={{ fontSize: 11, color: "#64748B", display: "block", marginBottom: 4 }}>SEARCH</label>
            <input style={input} placeholder="Case, facility, surgeon, tech"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {/* Sits with the other filters rather than below them: it is one,
              and separating it implied it worked differently. */}
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 7,
              cursor: "pointer", fontSize: 12.5, height: 34 }}>
              <input type="checkbox" checked={priorityOnly}
                onChange={(e) => setPriorityOnly(e.target.checked)} />
              <span style={{ whiteSpace: "nowrap" }}>24hr SignOff Facilities</span>
            </label>
            <button onClick={() => setEditingList(true)}
              style={{ background: "none", border: "none", padding: 0,
                cursor: "pointer", fontSize: 11.5, color: "#2563EB",
                textDecoration: "underline", textAlign: "left" }}>
              Edit list
            </button>
          </div>
        </div>

        <StatusCards statuses={MSO_STATUS} order={STATUS_ORDER}
          counts={counts} selected={statusF} onSelect={setStatusF} />

        {notice && (
          <div className="card" style={{ padding: 12, marginBottom: 12,
            borderLeft: "3px solid #059669", fontSize: 13,
            display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>{notice}</span>
            <button className="btn btn-sm" onClick={() => setNotice("")}>Dismiss</button>
          </div>
        )}
        {error && (
          <div className="card" style={{ padding: 12, marginBottom: 12,
            borderLeft: "3px solid #EF4444", fontSize: 13 }}>{error}</div>
        )}

        <div className="card" style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--gray-50, #F9FAFB)" }}>
                {[
                  ["status", "Status"],
                  ["patientId", "Case"],
                  ["serviceDate", "Service Date"],
                  ["techSignOffDate", "Tech SignOff"],
                  ["facility", "Facility"],
                  ["surgeonName", "Surgeon"],
                  ["techName", "Tech"],
                  ["clinicalSignOffDate", "Clinical SignOff"],
                  ["finalSignOffDate", "Final SignOff"],
                ].map(([key, label]) => (
                  <th key={key} style={{ ...th, cursor: "pointer" }} onClick={() => toggleSort(key)}>
                    {label}{" "}<SortArrow active={sort.key === key} dir={sort.dir} />
                  </th>
                ))}
                <th style={{ ...th, width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} style={{ ...td, textAlign: "center", padding: 32, color: "#64748B" }}>
                  Loading cases...
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={10} style={{ ...td, textAlign: "center", padding: 32, color: "#64748B" }}>
                  {cases.length === 0
                    ? "No cases have an MSO comparison yet."
                    : "No cases match these filters."}
                </td></tr>
              )}
              {!loading && filtered.map(c => (
                <tr key={c.patientId} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={td}><StatusPill status={c.status} /></td>
                  <td style={{ ...td, fontFamily: "var(--font-mono, monospace)", fontSize: 12.5 }}>
                    {c.patientId}
                  </td>
                  <td style={{ ...td, fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>
                    {c.serviceDate || "-"}
                  </td>
                  <td style={td}><SignOffCell date={c.techSignOffDate} /></td>
                  <td style={td}>{c.facility || "-"}</td>
                  <td style={td}>{c.surgeonName || "-"}</td>
                  <td style={td}>{c.techName || "-"}</td>
                  <td style={td}><SignOffCell date={c.clinicalSignOffDate} by={c.clinicalSignOffBy} /></td>
                  <td style={td}><SignOffCell date={c.finalSignOffDate} by={c.finalSignOffBy} /></td>
                  <td style={td}>
                    <button className="btn"
                      onClick={() => { setVerifying(c); setDocTab(SOURCE_TABS[0].key); setShowDocs(true); }}
                      style={c.finalSignOffDate ? rowBtn : verifyBtn}
                      title={c.finalSignOffDate
                        ? "Open this case. Both sign-offs are already recorded."
                        : c.clinicalSignOffDate
                          ? "Check the comparison and record the final sign-off"
                          : "Check the comparison and record the clinical sign-off"}>
                      {c.finalSignOffDate ? "View ↗" : "Verify ✎"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editingList && (
        <PriorityFacilityPicker
          onClose={() => setEditingList(false)}
          onSaved={() => { setEditingList(false); reload(); }}
        />
      )}

      {verifying && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,27,46,0.45)",
          display: "flex", justifyContent: "flex-end", zIndex: 50 }}
          onClick={() => setVerifying(null)}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: showDocs ? 1180 : 540, maxWidth: "100%", background: "var(--white)",
              height: "100%", display: "flex", boxShadow: "-8px 0 32px rgba(0,0,0,0.2)" }}>

            {/* Source documents. All three MSO reads, not just one. */}
            {showDocs && (
              <div style={{ flex: 1, minWidth: 0, borderRight: "1px solid var(--border)",
                display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                  alignItems: "center", gap: 8, padding: "8px 12px",
                  borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {SOURCE_TABS.map(t => {
                      const on = docTab === t.key;
                      const have = !!docUrls[t.key];
                      return (
                        <button key={t.key} onClick={() => setDocTab(t.key)}
                          title={have ? t.label : t.label + " was not uploaded for this case"}
                          style={{ padding: "5px 10px", fontSize: 11.5, borderRadius: 6,
                            cursor: "pointer", fontFamily: "var(--font-mono)",
                            border: "1px solid " + (on ? "#2563EB" : "var(--border)"),
                            background: on ? "#EFF6FF" : "var(--white)",
                            color: have ? (on ? "#1D4ED8" : "var(--text-secondary)") : "#94A3B8",
                            whiteSpace: "nowrap" }}>
                          {t.label}{!have && !docsLoading ? " · none" : ""}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0,
                    whiteSpace: "nowrap" }}>
                    {docUrls[docTab] && (
                      <a href={docUrls[docTab]} target="_blank" rel="noopener noreferrer"
                        className="btn btn-sm">Open</a>
                    )}
                    <button className="btn btn-sm" onClick={() => setShowDocs(false)}>Hide</button>
                  </div>
                </div>
                {docUrls[docTab] ? (
                  <iframe title={docTab} src={docUrls[docTab]} style={{ flex: 1, border: "none" }} />
                ) : (
                  <div style={{ flex: 1, display: "flex", alignItems: "center",
                    justifyContent: "center", padding: 24, textAlign: "center",
                    fontSize: 13, color: "#64748B" }}>
                    {docsLoading
                      ? "Loading documents..."
                      : "No " + (SOURCE_TABS.find(t => t.key === docTab) || {}).label
                        + " was uploaded for this case."}
                  </div>
                )}
              </div>
            )}

            {/* Verification */}
            <div style={{ flex: "0 1 540px", minWidth: 380,
              overflowY: "auto", padding: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                alignItems: "center", marginBottom: 2 }}>
                <h2 style={{ fontSize: 18, margin: 0 }}>Case {verifying.patientId}</h2>
                <div style={{ display: "flex", gap: 6 }}>
                  {!showDocs && <button className="btn btn-sm" onClick={() => setShowDocs(true)}>Show documents</button>}
                  {nextStage && (
                    <button className="btn btn-sm" onClick={doRegenerate}
                      disabled={!canRegenerate}
                      title={regenerating
                        ? "The comparison is running"
                        : "Run the comparison again, picking up any config changes"}>
                      {regenerating ? "Re-running…" : "Re-run"}
                    </button>
                  )}
                  <button className="btn btn-sm" onClick={() => setVerifying(null)}>Close</button>
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#64748B", marginBottom: 16 }}>
                {verifying.serviceDate || "no service date"}
                {verifying.facility ? " · " + verifying.facility : ""}
                {verifying.techName ? " · " + verifying.techName : ""}
              </div>

              {msoError && (
                <div style={{ padding: 10, marginBottom: 12, borderRadius: 6,
                  border: "1px solid #FCA5A5", background: "#FEF2F2",
                  fontSize: 12.5, color: "#991B1B" }}>{msoError}</div>
              )}
              {msoLoading && (
                <div style={{ fontSize: 13, color: "#64748B" }}>Loading the comparison...</div>
              )}

              {mso && (
                <>
                  <Panel title="Modality comparison"
                    sub={comparison.coverage_percent != null
                      ? comparison.coverage_percent + "% coverage"
                      : null}>
                    {modalityGroups.length === 0 && (
                      <Empty>No modality blocks in this run.</Empty>
                    )}
                    {modalityGroups.map(g => (
                      <ModalityGroup key={g.modality} group={g} />
                    ))}
                  </Panel>

                  <Panel title="Time comparison"
                    sub={timeSummary && timeSummary.score != null
                      ? timeSummary.earned + " of " + timeSummary.total + " agree"
                      : null}>
                    {timeChecks.length === 0 && (
                      <Empty>No times could be compared for this case.</Empty>
                    )}
                    {timeChecks.map(t => (
                      <div key={t.id} style={{ padding: "6px 0",
                        borderBottom: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <span style={{ fontSize: 12, minWidth: 128 }}>{t.label}</span>
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5,
                          color: "#64748B", flex: 1 }}>
                          {(t.chat_time || "").slice(11, 16) || "--:--"}
                          {" chat · "}
                          {(t.usmon_time || "").slice(11, 16) || "--:--"}
                          {" usmon"}
                        </span>
                        <span style={{ fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap",
                          color: TIME_STATUS_COLOR(t.status) }}
                          title={TIME_STATUS_HELP[t.status] || ""}>
                          {TIME_STATUS_LABEL(t)}
                        </span>
                        </div>

                        {/* What the parser actually read. A wrong time is
                            otherwise indistinguishable from a wrong column,
                            and the row already carries all of this. */}
                        {(t.matched_text || t.source) && (
                          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5,
                            color: "#94A3B8", marginTop: 3, paddingLeft: 136,
                            lineHeight: 1.45, overflowWrap: "anywhere" }}>
                            {[t.source, t.attribution === "rules" ? "rules" : t.attribution]
                              .filter(Boolean).join(" · ")}
                            {t.matched_keyword ? " · matched \"" + t.matched_keyword + "\"" : ""}
                            {t.matched_text ? " · " + t.matched_text : ""}
                          </div>
                        )}
                      </div>
                    ))}
                  </Panel>

                  <Panel title="Alert post"
                    sub={alerts.length ? alerts.length + (alerts.length === 1 ? " alert" : " alerts") : ""}>
                    {!postSentAvailable ? (
                      <Empty>
                        USMON does not expose post_sent yet, so this cannot be
                        checked. The column is read as soon as it carries data.
                      </Empty>
                    ) : verifying.postSent === null || verifying.postSent === undefined ? (
                      <Empty>No value recorded for this case.</Empty>
                    ) : (
                      (() => {
                        // Same row shape as the modality and time panels:
                        // label, both sources in the middle, verdict on the
                        // right. These are two independent records of the
                        // same event, the model reading the documents and
                        // USMON recording what the technologist marked, so
                        // the row that matters is the one where they differ.
                        const sent = !!verifying.postSent;
                        const n = alerts.length;
                        const unposted = !sent && n > 0;
                        return (
                          <div style={{ display: "flex", alignItems: "baseline",
                            gap: 8, padding: "6px 0" }}>
                            <span style={{ fontSize: 12, minWidth: 128 }}>Alert post</span>
                            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5,
                              color: "#64748B", flex: 1 }}>
                              {n === 0
                                ? "no alerts detected"
                                : n + (n === 1 ? " alert" : " alerts") + " detected"}
                              {" model · "}
                              {sent ? "post sent" : "no post"}
                              {" usmon"}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
                              color: unposted ? "#DC2626" : "#059669" }}
                              title={unposted
                                ? "The model found alerts in the case documents but USMON has no post recorded."
                                : sent
                                  ? "A post was recorded in USMON."
                                  : "Nothing was detected to post about."}>
                              {unposted ? "1 to review" : "✓"}
                            </span>
                          </div>
                        );
                      })()
                    )}

                    {/* What was posted about. These come from detect_alerts,
                        which reads the case documents before the QA checks
                        run, so they are what the model found rather than what
                        USMON recorded. */}
                    {alerts.length > 0 && (
                      <div style={{ marginTop: 12, borderTop: "1px solid var(--border)",
                        paddingTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                        {alerts.map((a, i) => (
                          <AlertRow key={i} alert={a} />
                        ))}
                      </div>
                    )}
                  </Panel>

                  <Panel title="Sign-off">
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
                      <Stage label="Clinical" entry={verifications.find(v => v.stage === "clinical")} />
                      <Stage label="Final" entry={verifications.find(v => v.stage === "final")} />
                    </div>

                    {nextStage ? (
                      <>
                        <textarea value={verifyNotes} rows={2}
                          onChange={(e) => setVerifyNotes(e.target.value)}
                          placeholder="Optional note for the record"
                          style={{ width: "100%", padding: "8px 10px", fontSize: 12.5,
                            border: "1px solid var(--border)", borderRadius: 6,
                            resize: "vertical", boxSizing: "border-box",
                            background: "var(--white)", color: "var(--text-primary)" }} />
                        <button className="btn btn-primary btn-sm"
                          disabled={!canVerify}
                          onClick={doVerify}
                          title={alreadyVerifiedByMe
                            ? "You recorded the clinical sign-off. The final one needs a second reviewer."
                            : "Record the " + nextStage + " sign-off"}
                          style={{ marginTop: 8 }}>
                          {verifyBusy ? "Recording..."
                            : nextStage === "clinical"
                              ? "Verify (clinical sign-off)"
                              : "Verify (final sign-off)"}
                        </button>
                        {alreadyVerifiedByMe && (
                          <div style={{ fontSize: 11.5, color: "#92400E", marginTop: 6 }}>
                            You recorded the clinical sign-off for this run. The
                            final sign-off has to come from a second reviewer.
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: 12.5, color: "#059669" }}>
                        Both sign-offs are recorded for version {mso.version}.
                      </div>
                    )}
                  </Panel>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* The count cards are the status filter; there is no separate control.
 * People do not reliably discover that a card is clickable, so the
 * affordance is both stated above the row and visible on the card: a hover
 * border in the status colour, and an unmistakable selected state with its
 * own way out. The line also changes to say how to clear, because a filter
 * you cannot find your way out of is worse than no filter.
 */
function StatusCards({ statuses, order, counts, selected, onSelect }) {
  const active = selected.length ? selected[0] : null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
        <span style={{ fontSize: 10.5, fontStyle: "italic", color: "var(--text-tertiary, #94A3B8)" }}>
          {active
            ? "Showing " + active.toLowerCase() + " only."
            : "Select a card to filter the table by status."}
        </span>
        {active && (
          <button onClick={() => onSelect([])}
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
              fontSize: 11.5, color: "#2563EB", textDecoration: "underline" }}>
            Clear filter
          </button>
        )}
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {order.map(k => {
          const cfg = statuses[k];
          const on = selected.includes(cfg.label);
          return (
            <div key={k} className="card" role="button" tabIndex={0}
              onClick={() => onSelect(on ? [] : [cfg.label])}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(on ? [] : [cfg.label]);
                }
              }}
              onMouseEnter={(e) => {
                if (!on) e.currentTarget.style.background = "var(--gray-50, #F8FAFC)";
              }}
              onMouseLeave={(e) => {
                if (!on) e.currentTarget.style.background = "";
              }}
              title={on ? "Clear this filter" : "Show only " + cfg.label.toLowerCase()}
              style={{ padding: "12px 16px", flex: "1 1 140px", cursor: "pointer",
                transition: "background 0.12s, box-shadow 0.12s",
                borderLeft: "3px solid " + cfg.color,
                boxShadow: on ? "inset 0 0 0 1.5px " + cfg.color : undefined,
                background: on ? "var(--gray-50, #F8FAFC)" : undefined }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span style={{ fontSize: 22, fontWeight: 600, color: cfg.color }}>
                  {counts[k]}
                </span>
                {on && (
                  <span style={{ fontSize: 10, fontFamily: "var(--font-mono)",
                    color: cfg.color }}>filtered</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "#64748B" }}>{cfg.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* A facility with no cases for this long reads as retired rather than quiet.
 * Twelve months is long enough not to mislabel a low-volume site, and short
 * enough to catch an id the EMR has replaced. */
const STALE_AFTER_DAYS = 365;

function monthYear(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

/* A raw date makes the reader do arithmetic on 178 rows. The question they
 * are actually asking is whether a facility is still in use, so answer that
 * and keep the date as the supporting detail. */
function facilityAge(lastUsed) {
  if (!lastUsed) return { stale: false, text: "No dated cases" };
  const then = new Date(lastUsed + "T00:00:00");
  if (isNaN(then.getTime())) return { stale: false, text: "Last case " + lastUsed };
  const days = (Date.now() - then.getTime()) / 86400000;
  if (days > STALE_AFTER_DAYS) {
    return { stale: true, text: "Not used since " + monthYear(lastUsed) };
  }
  return { stale: false, text: "Last case " + monthYear(lastUsed) };
}

/* The 24-hour sign-off list.
 *
 * Selection is from what the EMR actually writes, never typed: a name typed
 * by hand that does not match a real record would simply never match a case,
 * which reads as a broken filter rather than a wrong list. The id is shown
 * beside each name because a name does not identify a facility on its own.
 * MEDICAL CITY FRISCO, for instance, is two separate records.
 */
function PriorityFacilityPicker({ onClose, onSaved }) {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all([getFacilities(), getPriorityFacilities()])
      .then(([all, mine]) => {
        if (cancelled) return;
        setOptions((all && all.facilities) || []);
        setSelected((((mine && mine.facilities) || [])
          .map(f => String(f.facilityId)).filter(Boolean)));
      })
      .catch(e => { if (!cancelled) setError(e.message || "Could not load facilities"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? options.filter(o => (o.facility || "").toLowerCase().includes(q)
          || String(o.facilityId).includes(q))
      : options;
    // Chosen facilities first, so a long list never hides what is on it.
    return [...list].sort((a, b) => {
      const as = selected.includes(String(a.facilityId)) ? 0 : 1;
      const bs = selected.includes(String(b.facilityId)) ? 0 : 1;
      if (as !== bs) return as - bs;
      return (a.facility || "").localeCompare(b.facility || "");
    });
  }, [options, query, selected]);

  // The way this list goes wrong is silent: a site gets a new EMR id, the
  // list keeps matching the old one, and new cases quietly stop being
  // flagged. It has already happened to two of the five, so say it out loud.
  const staleSelected = useMemo(
    () => options.filter(o => selected.includes(String(o.facilityId))
      && facilityAge(o.lastUsed).stale),
    [options, selected]);

  const toggle = (fid) => setSelected(prev =>
    prev.includes(fid) ? prev.filter(x => x !== fid) : [...prev, fid]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await savePriorityFacilities(selected.map(fid => ({ facilityId: fid })));
      onSaved();
    } catch (e) {
      setError(e.message || "Could not save the list");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(15,27,46,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 620, maxWidth: "94vw", maxHeight: "86vh", background: "var(--white)",
          borderRadius: 10, display: "flex", flexDirection: "column",
          boxShadow: "0 16px 48px rgba(0,0,0,0.28)" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>24-hour sign-off facilities</h2>
          <p style={{ fontSize: 12, color: "#64748B", margin: "4px 0 0" }}>
            Facilities whose cases are expected to be signed off within a day.
            Selecting here changes what the filter on this page shows.
          </p>
        </div>

        <div style={{ padding: "10px 20px", borderBottom: "1px solid var(--border)",
          display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search facilities"
            style={{ ...input, flex: 1, minWidth: 180 }} />
          <span style={{ fontSize: 11.5, color: "#64748B", whiteSpace: "nowrap" }}>
            {selected.length} selected
          </span>
        </div>

        {staleSelected.length > 0 && (
          <div style={{ padding: "8px 20px", fontSize: 11.5, color: "#92400E",
            background: "#FFFBEB", borderBottom: "1px solid #FDE68A" }}>
            {staleSelected.length === 1
              ? "1 selected facility has had no cases in over a year. "
              : staleSelected.length + " selected facilities have had no cases in over a year. "}
            Keep them to cover older cases, or clear them if this list is only
            about current work.
          </div>
        )}

        <div style={{ flex: 1, overflowY: "auto", padding: "4px 8px" }}>
          {loading && (
            <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: "#64748B" }}>
              Loading facilities...
            </div>
          )}
          {!loading && shown.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: "#64748B" }}>
              No facilities match that search.
            </div>
          )}
          {!loading && shown.map(o => {
            const fid = String(o.facilityId);
            const on = selected.includes(fid);
            const age = facilityAge(o.lastUsed);
            return (
              <label key={fid}
                style={{ display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "7px 12px", borderRadius: 6, cursor: "pointer",
                  background: on ? "#F8FAFC" : undefined }}>
                <input type="checkbox" checked={on} onChange={() => toggle(fid)}
                  style={{ flexShrink: 0, marginTop: 2 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13 }}>{o.facility}</span>
                  <span style={{ display: "block", fontSize: 10.5,
                    color: age.stale ? "#B45309" : "#94A3B8",
                    fontWeight: age.stale ? 600 : 400 }}>
                    {age.text}
                  </span>
                </span>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5,
                  color: "#94A3B8", whiteSpace: "nowrap", textAlign: "right" }}>
                  Usmon Id {fid}
                  <span style={{ display: "block" }}>{o.caseCount} cases</span>
                </span>
              </label>
            );
          })}
        </div>

        {error && (
          <div style={{ padding: "8px 20px", fontSize: 12.5, color: "#991B1B",
            background: "#FEF2F2", borderTop: "1px solid #FCA5A5" }}>{error}</div>
        )}
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border)",
          display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || loading}>
            {saving ? "Saving..." : "Save list"}
          </button>
        </div>
      </div>
    </div>
  );
}

const SITE_VERDICT = {
  matched: { label: 'Present', mark: '✓', color: '#059669' },
  partial: { label: 'Partial', mark: '!', color: '#B45309' },
  unmatched: { label: 'Missing', mark: '✗', color: '#DC2626' },
};

/* A modality and the sites under it. The counts alone cannot be acted on:
   "5 of 6 matched" does not say which one is missing. The payload already
   carries the sites and a default_expanded flag the backend sets when a
   group holds a hard discrepancy, so the groups that need reading open
   themselves and the clean ones stay out of the way. */
function ModalityGroup({ group }) {
  const [open, setOpen] = useState(!!group.default_expanded);
  const items = [...(group.unmatched_items || []), ...(group.matched_items || [])];
  const hasDetail = items.length > 0;

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div onClick={() => hasDetail && setOpen(!open)}
        style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '6px 0',
          cursor: hasDetail ? 'pointer' : 'default' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12,
          fontWeight: 600, minWidth: 68 }}>{group.modality}</span>
        <span style={{ flex: 1, fontSize: 12, color: '#64748B' }}>
          {group.matched_count} of {group.total_count} sites matched
          {group.advisory_count ? ' · ' + group.advisory_count + ' advisory' : ''}
        </span>
        <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
          color: group.discrepancy_count > 0 ? '#DC2626' : '#059669' }}>
          {group.discrepancy_count > 0
            ? group.discrepancy_count + ' to review'
            : '✓'}
        </span>
        {hasDetail && (
          <span style={{ fontSize: 10, color: '#94A3B8', width: 10 }}>
            {open ? '▾' : '▸'}
          </span>
        )}
      </div>

      {open && hasDetail && (
        <div style={{ paddingBottom: 6 }}>
          {items.map(it => {
            const v = SITE_VERDICT[it.match_status] || SITE_VERDICT.unmatched;
            return (
              <div key={it.id} style={{ display: 'flex', alignItems: 'baseline',
                gap: 8, padding: '3px 0 3px 68px' }}>
                <span style={{ color: v.color, fontSize: 11, width: 10 }}>{v.mark}</span>
                <span style={{ fontSize: 12, flex: 1, minWidth: 0 }}>
                  {it.value}
                  {it.timestamp ? (
                    <span style={{ color: '#94A3B8', fontFamily: 'var(--font-mono)',
                      fontSize: 10.5 }}>{' · ' + it.timestamp}</span>
                  ) : null}
                  <span style={{ display: 'block', fontSize: 11, color: '#94A3B8',
                    lineHeight: 1.4 }}>{it.match_detail}</span>
                </span>
                <span style={{ fontSize: 10.5, fontFamily: 'var(--font-mono)',
                  color: v.color, whiteSpace: 'nowrap' }}>
                  {it.confidence === 'ADVISORY' ? 'advisory' : v.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Panel({ title, sub, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
        alignItems: "baseline", marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#64748B",
          letterSpacing: "0.04em", textTransform: "uppercase" }}>{title}</span>
        {sub && <span style={{ fontSize: 11, fontFamily: "var(--font-mono)",
          color: "#94A3B8" }}>{sub}</span>}
      </div>
      {children}
    </div>
  );
}

const ALERT_SEVERITY = {
  HIGH:   { fg: "#DC2626", bg: "#FEF2F2", bd: "#FECACA" },
  MEDIUM: { fg: "#B45309", bg: "#FFFBEB", bd: "#FDE68A" },
  LOW:    { fg: "#475569", bg: "#F8FAFC", bd: "#E2E8F0" },
};

/* One detected alert. The evidence is the exact quoted text the model found
   it in, so it is shown rather than summarised: a manager checking a post
   needs to see what was said, not a paraphrase of it. */
function AlertRow({ alert }) {
  const sev = ALERT_SEVERITY[String(alert.severity || "").toUpperCase()]
    || ALERT_SEVERITY.LOW;
  const facts = [alert.time, alert.modality].filter(Boolean).join(" · ");
  return (
    <div style={{ border: "1px solid " + sev.bd, background: sev.bg,
      borderRadius: 6, padding: "8px 10px", display: "flex",
      flexDirection: "column", gap: 4, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: sev.fg,
          letterSpacing: "0.04em" }}>
          {String(alert.type || "OTHER").replace(/_/g, " ")}
        </span>
        <span style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: sev.fg,
          border: "1px solid " + sev.bd, borderRadius: 3, padding: "0 4px" }}>
          {String(alert.severity || "LOW").toUpperCase()}
        </span>
        {alert.manual && (
          <span style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "#475569",
            border: "1px solid #CBD5E1", borderRadius: 3, padding: "0 4px" }}
            title="Entered by a reviewer, not detected by the model">
            MANUAL
          </span>
        )}
        {facts && <span style={{ fontSize: 11, color: "#64748B",
          fontFamily: "var(--font-mono)" }}>{facts}</span>}
      </div>
      {alert.description && (
        <div style={{ fontSize: 12.5, color: "var(--text-primary)", lineHeight: 1.45 }}>
          {alert.description}
        </div>
      )}
      {alert.evidence && (
        <div style={{ fontSize: 11.5, color: "#64748B", lineHeight: 1.45,
          borderLeft: "2px solid " + sev.bd, paddingLeft: 8, overflowWrap: "anywhere" }}>
          {alert.evidence}
        </div>
      )}
    </div>
  );
}

function Empty({ children }) {
  return <div style={{ fontSize: 12.5, color: "#94A3B8", lineHeight: 1.5 }}>{children}</div>;
}

function Stage({ label, entry }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, fontSize: 12.5 }}>
      <span style={{ minWidth: 64, color: "#64748B" }}>{label}</span>
      {entry ? (
        <span>
          <strong style={{ fontWeight: 600 }}>{entry.by}</strong>
          <span style={{ color: "#64748B" }}>
            {" · " + String(entry.at || "").slice(0, 10)}
          </span>
          {entry.notes && (
            <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 2 }}>{entry.notes}</div>
          )}
        </span>
      ) : (
        <span style={{ color: "#94A3B8" }}>not yet recorded</span>
      )}
    </div>
  );
}
